import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { FadeIn } from "@/components/Reveal";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { ShieldCheck } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ManageBooking() {
  const [params] = useSearchParams();
  const [id, setId] = useState(params.get("id") || "");
  const [email, setEmail] = useState("");
  const [booking, setBooking] = useState(null);
  const [loading, setLoading] = useState(false);

  const lookup = async (e) => {
    e?.preventDefault();
    if (!id) return toast.error("Enter your booking ID.");
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/bookings/lookup`, { params: { id, email: email || undefined } });
      setBooking(data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not find booking.");
    } finally { setLoading(false); }
  };

  // Auto-lookup when id is in URL query string
  useEffect(() => {
    if (params.get("id") && !booking) lookup();
    // eslint-disable-next-line
  }, []);

  const cancel = async () => {
    if (!window.confirm("Cancel this booking? Any payment will be refunded to the original method.")) return;
    try {
      const { data } = await axios.post(`${API}/bookings/${booking.id}/cancel`);
      toast.success(data.refund_inr ? `Booking cancelled. ₹${data.refund_inr.toLocaleString("en-IN")} refunded.` : "Booking cancelled.");
      setBooking({ ...booking, status: "cancelled", payment_status: data.refund_inr ? "refunded" : booking.payment_status });
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not cancel.");
    }
  };

  return (
    <main data-testid="manage-booking-page" className="pt-32 pb-24">
      <section className="mx-auto max-w-[900px] px-6 lg:px-12">
        <FadeIn>
          <div className="stamp mb-6">Vol · VIII · Manage</div>
          <h1 className="font-display text-5xl md:text-7xl leading-[0.95] text-[#F2E7D0]">
            Manage your <em className="italic text-[#D97645]">booking</em>.
          </h1>
          <p className="mt-6 text-[#B8A891] text-base">
            Look up any reservation with the booking ID from your confirmation email.
          </p>

          {!booking && (
            <form onSubmit={lookup} className="mt-10 space-y-4 max-w-md">
              <div>
                <Label className="font-mono-label text-[#B8A891]">Booking ID</Label>
                <Input data-testid="lookup-id" value={id} onChange={(e) => setId(e.target.value)} className="mt-2 bg-transparent border-white/20 rounded-none h-12" placeholder="e.g. 8a1e7cb2-…" />
              </div>
              <div>
                <Label className="font-mono-label text-[#B8A891]">Email on booking (optional)</Label>
                <Input data-testid="lookup-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} className="mt-2 bg-transparent border-white/20 rounded-none h-12" />
              </div>
              <Button type="submit" data-testid="lookup-submit" disabled={loading} className="rounded-none bg-[#D97645] hover:bg-[#C0602F] text-[#F2E7D0] font-mono-label h-12 px-8">
                {loading ? "Looking up…" : "Find my booking"}
              </Button>
            </form>
          )}

          {booking && (
            <div className="mt-10 space-y-6" data-testid="lookup-result">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 border-t border-white/10 pt-8">
                <div><div className="chapter-num">Rooms</div><div className="mt-2 font-display text-2xl text-[#F2E7D0]">{booking.room_type}</div></div>
                <div><div className="chapter-num">Guests</div><div className="mt-2 text-[#F2E7D0]">{booking.guests}</div></div>
                <div><div className="chapter-num">Dates</div><div className="mt-2 text-[#F2E7D0]">{booking.check_in} → {booking.check_out}</div></div>
                <div><div className="chapter-num">Status</div><div className="mt-2 text-[#F2E7D0] capitalize">{booking.status} · {booking.payment_status}</div></div>
                <div><div className="chapter-num">Total</div><div className="mt-2 text-[#F2E7D0]">₹ {(booking.total_inr || 0).toLocaleString("en-IN")}</div></div>
                <div><div className="chapter-num">Amount paid</div><div className="mt-2 text-[#F2E7D0]">₹ {(booking.amount_paid_inr || 0).toLocaleString("en-IN")}</div></div>
              </div>
              {booking.status !== "cancelled" ? (
                <div className="border border-white/10 p-6" data-testid="cancel-panel">
                  <div className="chapter-num mb-2">Cancellation Policy</div>
                  <p className="text-[#B8A891] text-sm leading-relaxed">Free cancellation any time <span className="text-[#F2E7D0]">before your check-in date</span> — full refund. On/after check-in, zero refund.</p>
                  <Button data-testid="cancel-btn" onClick={cancel} variant="outline" className="mt-4 rounded-none bg-transparent border border-white/30 hover:border-red-400 hover:text-red-400 text-[#F2E7D0] font-mono-label h-10 px-6">
                    Cancel booking
                  </Button>
                </div>
              ) : (
                <div className="border border-white/10 p-6 text-[#B8A891]" data-testid="cancelled-notice">
                  <ShieldCheck size={18} className="text-[#D97645] inline mr-2" />
                  Booking cancelled. {booking.payment_status === "refunded" ? "Refund has been initiated to your original payment source (5–7 working days)." : ""}
                </div>
              )}
            </div>
          )}
        </FadeIn>
      </section>
    </main>
  );
}
